import { Inject, inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { StoreSearchApiResponse } from '../models/store-search-api-response';

import {
  ProductApiService,
  ProductInterface,
  ProductListResponseInterface,
  Store,
  storeCategories,
  StoreCategory,
  StoreRestrictionFields,
  StoreSort,
} from '@client-monorepo/daily-fintech/stores';
import { ProductSearchApiResponse } from '../models/product-search-api-response';
import {
  ApiService,
  OrderTypes,
  RequestBuilder,
  RequestTypeEnum,
  Restriction,
  RestrictionTypes,
  SearchPayloadInterface,
} from '@client-monorepo/common/network';
import { GeoQueryResponse } from '../models/geo-query.request';
import { DefaultRestriction } from '../models/stores-filters.model';

@Injectable({
  providedIn: 'root',
})
export class StoresApiService {
  defaultRestriction = DefaultRestriction;
  apiService = inject(ApiService);
  productService = inject(ProductApiService);

  constructor(@Inject('APP_ENV') private environment: { [key: string]: string }) {}

  getAllStores(payload: SearchPayloadInterface<StoreRestrictionFields> | undefined = undefined, size = 20): Observable<Store[]> {
    if (payload) {
      payload.restrictions = [...payload.restrictions, this.defaultRestriction];
    } else {
      payload = {
        restrictions: [this.defaultRestriction],
        orders: [],
      };
    }
    const page = payload.page || 0;
    const calculatedSize = payload.size || size;
    const request = new RequestBuilder(RequestTypeEnum.POST, 'dpx/stores/search?' + `page=${page}&size=${calculatedSize}`, payload);
    return this.apiService.call<StoreSearchApiResponse>(request).pipe(map((res) => res.stores));
  }

  searchStores(
    body: SearchPayloadInterface<StoreRestrictionFields>,
    page = 0,
    size = 20,
    count = false,
  ): Observable<StoreSearchApiResponse> {
    body.restrictions = [...body.restrictions, this.defaultRestriction];
    if (body.orders.length === 0) {
      body.orders = [{ field: StoreSort.PRIORITY, order: OrderTypes.ASC }];
    }
    let request = new RequestBuilder(
      RequestTypeEnum.POST,
      'dpx/stores/search' + '?page=' + page + '&size=' + size + (count ? '&count=true' : ''),
      body ? body : {},
    );
    request = request.enableCache(30 * 1000);
    return this.apiService.call(request);
  }

  private getStoreBasedOnCollectionField(
    field: StoreRestrictionFields,
    value: any,
    page = 0,
    size = 20,
    orders: any[] = [{ field: StoreSort.PRIORITY, order: OrderTypes.ASC }],
  ): Observable<StoreSearchApiResponse> {
    const restrictions: Restriction<StoreRestrictionFields>[] = [
      {
        type: RestrictionTypes.COLLECTION,
        field,
        operation: 'eq',
        values: [value],
      },
      this.defaultRestriction,
    ];
    const request = new RequestBuilder(RequestTypeEnum.POST, 'dpx/stores/search' + '?page=' + page + '&size=' + size, {
      restrictions: restrictions,
      orders: orders,
    }).enableCache(5 * 1000 * 60);
    return this.apiService.call(request);
  }

  getStore(title: string): Observable<Store | undefined> {
    const payload: SearchPayloadInterface<StoreRestrictionFields> = {
      restrictions: [
        {
          type: RestrictionTypes.SIMPLE,
          field: StoreRestrictionFields.TITLE,
          value: title,
          operation: 'eq',
        },
      ],
      orders: [],
    };
    const request = new RequestBuilder(RequestTypeEnum.POST, 'dpx/stores/search', payload);
    return this.apiService.call<StoreSearchApiResponse>(request).pipe(
      map((res) => res.stores),
      map((stores: Store[]) => stores[0]),
    );
  }

  getAllCategories(): Observable<StoreCategory[]> {
    const allCategories = storeCategories;
    return new Observable((observer) => {
      observer.next(allCategories);
    });
  }

  getStoreProducts(website: string, keyword = '', page = 0, size = 10): Observable<ProductSearchApiResponse> {
    let url = `dpx/search?page=${page}&size=${size}&host=${website}&available=true`;
    url = keyword === '' ? url : url + '&keyword=' + keyword;
    const request = new RequestBuilder(RequestTypeEnum.GET, url);
    return this.apiService.call<ProductSearchApiResponse>(request);
  }

  getProductsBasedOnQuery(query: string, method = RequestTypeEnum.GET, body: any = undefined): Observable<ProductInterface[]> {
    const request =
      method === RequestTypeEnum.GET
        ? new RequestBuilder(RequestTypeEnum.GET, query)
        : new RequestBuilder(RequestTypeEnum.POST, query, body);
    return this.apiService
      .call<ProductInterface[]>(request)
      .pipe(
        map((response: any) => {
          this.productService.setPriceFacet(response as ProductListResponseInterface);
          return response;
        }),
      )
      .pipe(map((res: any) => (res.items ? res.items.map((i: { product: ProductInterface }) => i.product) : res.products)));
  }

  getStoresByGeo(query: GeoQueryResponse): Observable<StoreSearchApiResponse> {
    let url = 'dpx/stores/search/geo';
    if ('website' == this.environment['appName']) {
      url = 'stores/search/geo';
    }
    const request = new RequestBuilder(RequestTypeEnum.POST, url, query);
    return this.apiService.call<StoreSearchApiResponse>(request).pipe(
      map((res) => {
        const newRes: StoreSearchApiResponse = { ...res, stores: [] };
        res.stores.forEach((store) => {
          store.branches.forEach((branch) => {
            newRes.stores.push({ ...store, branches: [branch] });
          });
        });
        return newRes;
      }),
    );
  }

  getPopularStores(page = 0, size = 10): Observable<Array<Store>> {
    const payload: SearchPayloadInterface<StoreRestrictionFields> = {
      restrictions: [],
      orders: [
        {
          order: OrderTypes.ASC,
          field: 'priority',
        },
      ],
      page,
      size,
    };
    return this.getAllStores(payload);
  }

  getStoresByTitles(titles: string[], page = 0, size = 10): Observable<Array<Store>> {
    const payload: SearchPayloadInterface<StoreRestrictionFields> = {
      restrictions: [{ type: RestrictionTypes.COLLECTION, field: StoreRestrictionFields.TITLE, values: titles }],
      orders: [
        {
          order: OrderTypes.ASC,
          field: 'priority',
        },
      ],
      page,
      size,
    };
    return this.getAllStores(payload);
  }
}
